import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { bumpBrandingCache, DEFAULT_COVER_IMAGE_URL } from "@/lib/branding";
import PageHero from "@/components/PageHero";
import MediaSettingsCard from "@/components/MediaSettingsCard";
import ProposalContentEditor from "@/components/ProposalContentEditor";
import PaymentDetailsCard from "@/components/PaymentDetailsCard";
import { useCapabilities } from "@/context/PermissionsContext";
import {
  Image as ImageIcon, Save, RotateCcw, Loader2, CheckCircle2, Link as LinkIcon,
  ExternalLink, FileText,
} from "lucide-react";
import { toast } from "sonner";

/**
 * iter63 — Admin Settings.
 *
 * Single tab today (Branding). Kept as its own page + sidebar item so future
 * studio-wide settings (invoice footer, currency preferences, WhatsApp
 * signature) have an obvious home.
 *
 * The Branding tab wraps `GET/PATCH /api/admin/branding` — a one-doc Mongo
 * settings collection that both `/login` and `/inquiry` read publicly. The
 * `useBranding` hook on those pages picks the new URL up on next mount; a
 * "Preview live" button opens each page in a new tab so the studio owner
 * can eyeball the change before advertising the new URL to clients.
 */
export default function Settings() {
  // iter176 — Tabbed layout: keep every existing settings card intact
  // under "General"; add a new "Proposal Content" tab that hosts the
  // admin editor. Nothing else moves.
  const proposalCaps = useCapabilities("proposal");
  const [activeTab, setActiveTab] = useState("general");
  const tabs = [
    { key: "general", label: "General" },
    // Show the tab whenever the user can view proposal content. The
    // editor itself hides destructive actions when `canEdit` is false.
    ...(proposalCaps.canView
      ? [{ key: "proposal-content", label: "Proposal Content" }]
      : []),
    // iter178 — Admin-only Payment Details tab.
    { key: "payment-details", label: "Payment Details" },
  ];

  const [url, setUrl] = useState("");
  const [initialUrl, setInitialUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [meta, setMeta] = useState(null); // {is_default, updated_at}

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/public/branding");
        if (!mounted) return;
        // If the stored URL is the shipped default (is_default=true), leave
        // the input blank so the admin sees "shipped default" state clearly
        // and doesn't accidentally re-save the fallback URL as a manual value.
        const effective = data.is_default ? "" : (data.cover_image_url || "");
        setUrl(effective);
        setInitialUrl(effective);
        setMeta(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const dirty = url.trim() !== initialUrl.trim();
  const previewUrl = url.trim() || DEFAULT_COVER_IMAGE_URL;

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/branding", { cover_image_url: url.trim() });
      bumpBrandingCache(data);
      setMeta(data);
      setInitialUrl(url.trim());
      toast.success(
        (url || "").trim()
          ? "Cover image updated"
          : "Cover reset to default"
      );
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Reset the cover image to the shipped default?")) return;
    setUrl("");
    setErr("");
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/branding", { cover_image_url: "" });
      bumpBrandingCache(data);
      setMeta(data);
      setInitialUrl("");
      toast.success("Cover reset to default");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHero
        eyebrow="Kahani Studio · Admin Only"
        title="Settings"
        subtitle="Studio-wide preferences. Changes take effect for every visitor the next time they open the page."
      />

      {tabs.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b border-stone-200 -mb-px" data-testid="settings-tabs">
          {tabs.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                  active ? "text-rose-700 border-rose-700" : "text-stone-500 border-transparent hover:text-stone-800"
                }`}
                data-testid={`settings-tab-${t.key}`}
              >
                {t.key === "proposal-content" && <FileText className="w-3.5 h-3.5" strokeWidth={1.8} />}
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {activeTab === "proposal-content" ? (
        <section className="card p-6" data-testid="settings-proposal-content">
          <div className="mb-4">
            <div className="editorial-eyebrow">Global · Applies to NEW proposals only</div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              Proposal Content Editor
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              Edit the Deliverables sections and Terms &amp; Conditions shown on every new client proposal. Existing proposals keep the content they were created with.
            </p>
          </div>
          <ProposalContentEditor mode="global" />
        </section>
      ) : activeTab === "payment-details" ? (
        <PaymentDetailsCard />
      ) : (
        <>
      {/* Branding card */}
      <section className="card p-6 space-y-6" data-testid="settings-branding">
        <div className="flex items-start gap-3">
          <ImageIcon className="w-5 h-5 text-rose-700 mt-1" strokeWidth={1.6} />
          <div className="flex-1">
            <div className="editorial-eyebrow">Branding</div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              Cover image · Login &amp; Inquiry pages
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              Paste any public image URL (JPG / PNG / WEBP). Leave blank to use the shipped default portrait.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-stone-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* URL Input */}
              <div className="space-y-3">
                <label className="label">Image URL</label>
                <div className="relative">
                  <LinkIcon className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                  <input
                    className="input pl-9"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://cdn.example.com/hero.jpg"
                    data-testid="settings-branding-url"
                  />
                </div>
                {meta?.is_default ? (
                  <div className="text-[11px] text-stone-500 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" strokeWidth={2.4} />
                    Currently showing shipped default image.
                  </div>
                ) : (
                  <div className="text-[11px] text-stone-500">
                    {meta?.updated_at ? `Last updated ${new Date(meta.updated_at).toLocaleString("en-IN")}.` : ""}
                  </div>
                )}

                <div className="pt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    className={`btn btn-primary ${(!dirty || saving) ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid="settings-branding-save"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.8} />}
                    Save cover
                  </button>
                  <button
                    type="button"
                    onClick={resetToDefault}
                    disabled={saving || meta?.is_default}
                    className="btn btn-outline"
                    data-testid="settings-branding-reset"
                  >
                    <RotateCcw className="w-4 h-4" strokeWidth={1.8} /> Reset to default
                  </button>
                </div>

                <div className="pt-2 flex flex-wrap gap-2 text-xs">
                  <a
                    href="/login"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-rose-700 hover:text-rose-900 font-semibold"
                    data-testid="settings-preview-login"
                  >
                    <ExternalLink className="w-3 h-3" strokeWidth={2} /> Preview on Login
                  </a>
                  <span className="text-stone-300">·</span>
                  <a
                    href="/inquiry"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-rose-700 hover:text-rose-900 font-semibold"
                    data-testid="settings-preview-inquiry"
                  >
                    <ExternalLink className="w-3 h-3" strokeWidth={2} /> Preview on Inquiry
                  </a>
                </div>

                {err && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="settings-error">
                    {err}
                  </div>
                )}
              </div>

              {/* Live preview */}
              <div>
                <div className="editorial-eyebrow mb-2">Live Preview</div>
                <div className="relative rounded-xl overflow-hidden aspect-[3/4] border border-stone-200 bg-stone-100">
                  <img
                    key={previewUrl}
                    src={previewUrl}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                    data-testid="settings-branding-preview"
                    onError={(e) => {
                      // Broken URL — swap to a text placeholder so admin can
                      // see the failure without opening devtools.
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-rose-900/60 via-rose-800/25 to-amber-700/20 pointer-events-none" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="text-[9px] uppercase tracking-[0.25em] opacity-90 mb-1">Get a Personalised Estimate</div>
                    <div className="font-heading text-lg leading-tight font-semibold">
                      Every wedding has a story. Tell us yours.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* iter71 — Media Settings block (Privacy / Downloads / Watermark) */}
      <MediaSettingsCard />

      {/* iter142 — Studio Branding (logo + colors + contacts) */}
      <StudioBrandingCard />
        </>
      )}
    </div>
  );
}

// ==========================================================================
// iter142 — Studio Branding editor.
// Renders inside Settings, below Cover Image + Media Settings. Manages the
// singleton `studio_branding` doc via GET/PUT /api/settings/branding.
// Live preview shows exactly how the logo + studio name will appear on
// the Public Proposal hero, plus the primary/accent color chips.
// ==========================================================================
function StudioBrandingCard() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/branding");
        setB(data);
      } catch (e) {
        setErr(e.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const setField = (k) => (v) => setB((prev) => ({ ...(prev || {}), [k]: v }));

  const handleFileUpload = async (field, file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      setErr(`${file.name} is too large — please pick a file under 500 KB (${Math.round(file.size / 1024)} KB uploaded).`);
      return;
    }
    setErr("");
    const reader = new FileReader();
    reader.onload = () => setField(field)(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true); setErr(""); setOk("");
    try {
      const { data } = await api.put("/settings/branding", {
        studio_name: b.studio_name,
        tagline: b.tagline,
        logo_url: b.logo_url,
        logo_dark_url: b.logo_dark_url,
        logo_light_url: b.logo_light_url,
        favicon_url: b.favicon_url,
        primary_color: b.primary_color,
        accent_color: b.accent_color,
        website: b.website,
        instagram: b.instagram,
        whatsapp_number: b.whatsapp_number,
        email: b.email,
        footer_copyright: b.footer_copyright,
      });
      setB(data);
      setOk("Branding saved. Public pages will refresh automatically.");
      // Invalidate the shared branding cache so admin bookings + public
      // pages read the new logo/name on next render.
      try {
        const mod = await import("@/lib/branding");
        mod.bumpBrandingCache?.();
      } catch { /* noop */ }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setOk(""), 4000);
    }
  };

  if (!b) {
    return (
      <section className="card p-6 space-y-4">
        <div className="text-sm text-stone-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading branding…</div>
      </section>
    );
  }

  return (
    <section className="card p-6 space-y-6" data-testid="settings-studio-branding">
      <div className="flex items-start gap-3">
        <ImageIcon className="w-5 h-5 text-rose-700 mt-1" strokeWidth={1.6} />
        <div className="flex-1">
          <div className="editorial-eyebrow">Studio Branding</div>
          <h2 className="font-heading text-2xl font-semibold text-stone-900">Logo, colors &amp; contact</h2>
          <p className="text-sm text-stone-500 mt-1">Everything below is applied automatically to proposals, gallery pages, client tracker, and PDFs. Nothing is hardcoded.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: form */}
        <div className="space-y-4">
          <BrandField label="Studio Name" value={b.studio_name} onChange={setField("studio_name")} testid="branding-studio-name" />
          <BrandField label="Tagline" value={b.tagline} onChange={setField("tagline")} testid="branding-tagline" />
          <BrandLogoField label="Studio Logo (default)" hint="Used on light backgrounds. PNG/SVG/WEBP with transparent background, under 500 KB."
            value={b.logo_url} onChange={setField("logo_url")}
            onFile={(f) => handleFileUpload("logo_url", f)} testid="branding-logo" />
          <BrandLogoField label="Dark Logo (optional)" hint="Explicit dark version for light backgrounds. If empty, we use the default logo."
            value={b.logo_dark_url} onChange={setField("logo_dark_url")}
            onFile={(f) => handleFileUpload("logo_dark_url", f)} testid="branding-logo-dark" />
          <BrandLogoField label="Light Logo (optional)" hint="Ivory/white version for dark backgrounds (proposal hero, client tracker header)."
            value={b.logo_light_url} onChange={setField("logo_light_url")}
            onFile={(f) => handleFileUpload("logo_light_url", f)} testid="branding-logo-light" />
          <BrandLogoField label="Favicon (optional)" hint="Square ICO/PNG shown in the browser tab. 32×32 or 64×64 recommended."
            value={b.favicon_url} onChange={setField("favicon_url")}
            onFile={(f) => handleFileUpload("favicon_url", f)} testid="branding-favicon" />
          <div className="grid grid-cols-2 gap-3">
            <BrandColorField label="Primary Color" value={b.primary_color} onChange={setField("primary_color")} testid="branding-primary-color" />
            <BrandColorField label="Accent Color" value={b.accent_color} onChange={setField("accent_color")} testid="branding-accent-color" />
          </div>
          <BrandField label="Website" placeholder="https://…" value={b.website} onChange={setField("website")} testid="branding-website" />
          <BrandField label="Instagram" placeholder="https://instagram.com/…" value={b.instagram} onChange={setField("instagram")} testid="branding-instagram" />
          <BrandField label="WhatsApp Number" placeholder="+91…" value={b.whatsapp_number} onChange={setField("whatsapp_number")} testid="branding-whatsapp" />
          <BrandField label="Email" placeholder="hello@studio.com" value={b.email} onChange={setField("email")} testid="branding-email" />
          <BrandField label="Footer Copyright" value={b.footer_copyright} onChange={setField("footer_copyright")} testid="branding-footer" />
        </div>

        {/* RIGHT: live preview */}
        <div className="space-y-3" data-testid="branding-preview">
          <div className="editorial-eyebrow">Live preview</div>
          <div className="bg-[#fbf5ea] rounded-2xl p-6 border border-stone-200">
            <div className="text-center">
              {b.logo_url ? (
                <img src={b.logo_url} alt={b.studio_name || "logo"} className="mx-auto max-h-[80px] object-contain" data-testid="branding-preview-logo" />
              ) : (
                <div className="font-serif text-2xl text-[#3b2f24]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="branding-preview-logo-fallback">
                  {b.studio_name || "Studio Name"}
                </div>
              )}
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63] mt-4">{b.studio_name}</div>
              <div className="font-serif text-2xl text-[#3b2f24] mt-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                A proposal, crafted with care
              </div>
              {b.tagline && (
                <div className="text-sm text-[#7a6853] italic mt-3">{b.tagline}</div>
              )}
            </div>
            <div className="mt-6 flex items-center justify-center gap-3">
              <ColorSwatch color={b.primary_color} label="Primary" />
              <ColorSwatch color={b.accent_color} label="Accent" />
            </div>
          </div>
          <div className="text-[11px] text-stone-500 italic text-center">This preview mirrors the top of every public proposal.</div>
        </div>
      </div>

      {(err || ok) && (
        <div className={`text-sm rounded-md px-3 py-2 border ${err ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
          {err || ok}
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="branding-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Save branding
        </button>
      </div>
    </section>
  );
}

function BrandField({ label, value, onChange, placeholder = "", testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

function BrandColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#b18860"} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded border border-stone-200 cursor-pointer" data-testid={`${testid}-picker`} />
        <input className="input flex-1 font-mono text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#b18860" data-testid={testid} />
      </div>
    </div>
  );
}

function BrandLogoField({ label, hint, value, onChange, onFile, testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      {hint && <div className="text-[11px] text-stone-500 mb-1.5">{hint}</div>}
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt={label} className="w-16 h-16 object-contain bg-white border border-stone-200 rounded" data-testid={`${testid}-thumb`} />
        ) : (
          <div className="w-16 h-16 border border-dashed border-stone-300 rounded flex items-center justify-center text-stone-400"><ImageIcon className="w-5 h-5" /></div>
        )}
        <div className="flex-1 space-y-2">
          <input className="input text-xs" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste an image URL or use Upload" data-testid={testid} />
          <div className="flex items-center gap-2">
            <label className="btn btn-outline btn-sm cursor-pointer">
              Upload
              <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid={`${testid}-file`} />
            </label>
            {value && <button type="button" className="btn btn-outline btn-sm" onClick={() => onChange("")} data-testid={`${testid}-clear`}>Clear</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorSwatch({ color, label }) {
  return (
    <div className="text-center">
      <div className="w-12 h-12 rounded-full border border-stone-300 shadow-sm" style={{ backgroundColor: color || "#b18860" }} />
      <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1">{label}</div>
      <div className="text-[10px] font-mono text-stone-600">{color}</div>
    </div>
  );
}
